import React from 'react';
import { Link } from 'react-router-dom';
import { SectionHead } from './SectionHead';
import Faq from './Faq';
import Button from './Button';
import SiblingLinks from './SiblingLinks';

export const formatEvidence = (evidence) => {
  if (typeof evidence === 'string' || !evidence) return evidence;
  return (
    <>
      <strong className="cy-emph">{evidence.lead}</strong>
      {' \u2014 '}
      {evidence.rest}
    </>
  );
};

export const CriteriaSchedule = ({ rows, term, primary, secondary }) => (
  <dl className="cy-schedule">
    {rows.map((row) => (
      <div className="cy-schedule__row" key={row[term]}>
        <dt>{row[term]}</dt>
        <dd>
          {primary && row[primary] ? <p className="cy-schedule__what">{row[primary]}</p> : null}
          {secondary && row[secondary] ? <p className="cy-schedule__why">{row[secondary]}</p> : null}
        </dd>
      </div>
    ))}
  </dl>
);

export const DecisionStops = ({ items, featured = false }) => (
  <ul className={`cy-register${featured ? ' cy-register--featured' : ''}`}>
    {items.map((item) => (
      <li className="cy-register__item" key={item.title}>
        <h3 className="cy-register__title">{item.title}</h3>
        <p className="cy-register__body">{item.body}</p>
      </li>
    ))}
  </ul>
);

export const LensList = ({ items, gateLabel }) => (
  <div className="cy-lens">
    <ol className="cy-lens-toc">
      {items.map((item) => (
        <li key={item.title} className={item.gate ? 'is-gate' : undefined}>
          {item.title}
        </li>
      ))}
    </ol>
    <ol className="cy-lens-list">
      {items.map((item) => (
        <li className={`cy-lens__item${item.gate ? ' is-gate' : ''}`} key={item.title}>
          <header className="cy-lens__head">
            {item.gate && gateLabel ? <span className="cy-lens__gate">{gateLabel}</span> : null}
            <h3 className="cy-lens__title">{item.title}</h3>
          </header>
          <dl className={`cy-lens__fields cy-lens__fields--${item.fields.length}`}>
            {item.fields.map((field, i) => {
              const tone = field.tone || (i === item.fields.length - 1 ? 'judgement' : undefined);
              return (
                <div
                  className={`cy-lens__field${tone ? ` cy-lens__field--${tone}` : ''}`}
                  key={field.label}
                >
                  <dt>{field.label}</dt>
                  <dd>{field.value}</dd>
                </div>
              );
            })}
          </dl>
        </li>
      ))}
    </ol>
  </div>
);

export const GateList = ({ rows, passLabel, stopLabel, featuredLabel }) => (
  <ol className="cy-gates">
    {rows.map((row, i) => (
      <li className={`cy-gates__item${row.featured ? ' is-featured' : ''}`} key={row.gate}>
        <span className="cy-gates__id">G{i + 1}</span>
        <div className="cy-gates__body">
          <header className="cy-gates__head">
            {row.featured && featuredLabel ? <span className="cy-lens__gate">{featuredLabel}</span> : null}
            <h3 className="cy-gates__title">{row.gate}</h3>
          </header>
          <dl className="cy-gates__signals">
            <div className="cy-gates__pass">
              <dt>{passLabel}</dt>
              <dd>{row.evidence}</dd>
            </div>
            <div className="cy-gates__stop">
              <dt>{stopLabel}</dt>
              <dd>{row.stop}</dd>
            </div>
          </dl>
        </div>
      </li>
    ))}
  </ol>
);

export const CapabilityList = ({ rows, behaviourLabel, evidenceLabel, forbiddenLabel }) => (
  <ol className="cy-cap">
    {rows.map((row) => (
      <li className="cy-cap__item" key={row.cap}>
        <h3 className="cy-cap__title">{row.cap}</h3>
        <p className="cy-cap__line">
          <span className="cy-matrix__label">{behaviourLabel}</span>
          {row.behaviour}
        </p>
        <p className="cy-cap__line">
          <span className="cy-matrix__label">{evidenceLabel}</span>
          {row.evidence}
        </p>
        <p className="cy-cap__forbidden">
          <span className="cy-cap__forbidden-label">{forbiddenLabel}</span>
          {row.forbidden}
        </p>
      </li>
    ))}
  </ol>
);

export const RoleMatrix = ({ rows, titleKey, fields, notKey, notLabel }) => (
  <div className="cy-matrix">
    {rows.map((row) => (
      <article className="cy-matrix__item" key={row[titleKey]}>
        <h3 className="cy-matrix__title">{row[titleKey]}</h3>
        {fields.map((field) => (
          <p className="cy-matrix__line" key={field.key}>
            <span className="cy-matrix__label">{field.label}</span>
            {row[field.key]}
          </p>
        ))}
        {notKey ? (
          <p className="cy-matrix__not">
            <span className="cy-matrix__label">{notLabel}</span>
            {row[notKey]}
          </p>
        ) : null}
      </article>
    ))}
  </div>
);

export const MaterialPair = ({ minimumTitle, laterTitle, minimum, later }) => (
  <div className="cy-grid cy-grid--2">
    <div className="cy-card">
      <h3 className="cy-card-title">{minimumTitle}</h3>
      <ul className="cy-checklist">
        {minimum.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
    <div className="cy-card">
      <h3 className="cy-card-title">{laterTitle}</h3>
      <ul className="cy-checklist">
        {later.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  </div>
);

export const ReadinessList = ({ rows, evidenceLabel, investLabel, pilotLabel }) => (
  <ol className="cy-readiness">
    {rows.map((row, i) => (
      <li className="cy-readiness__item" key={row.stage}>
        <span className="cy-stop-list__num" aria-hidden="true">
          {String(i + 1).padStart(2, '0')}
        </span>
        <div>
          <h3 className="cy-readiness__title">{row.stage}</h3>
          <p className="cy-readiness__evidence">
            <span className="cy-matrix__label">{evidenceLabel}</span>
            {row.evidence}
          </p>
          <p className="cy-readiness__path">
            <span className="cy-emph">{investLabel}</span>
            {' — '}
            {row.invest}
          </p>
          <p className="cy-readiness__path">
            <span className="cy-emph">{pilotLabel}</span>
            {' — '}
            {row.pilot}
          </p>
        </div>
      </li>
    ))}
  </ol>
);

export const PillarClose = ({
  faqId,
  ctaId,
  faqTitle,
  faqItems,
  idPrefix,
  cta,
  ctaTo,
  alt,
  altTo,
  siblings,
}) => (
  <>
    <section className="cy-section cy-section--alt" id={faqId}>
      <div className="cy-container cy-container--reading">
        <SectionHead title={faqTitle} />
        <Faq items={faqItems} idPrefix={idPrefix} />
      </div>
    </section>
    <section className="cy-section cy-section--cta" id={ctaId}>
      <div className="cy-container cy-cta-band">
        <div className="cy-cta-band__actions cy-cta-band__actions--flush">
          <Button to={ctaTo}>{cta}</Button>
          <Link className="cy-link" to={altTo}>
            {alt}
          </Link>
        </div>
        <SiblingLinks items={siblings} />
      </div>
    </section>
  </>
);
